import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { 
  DollarSign, Music, BarChart3, Shield, Users, Calendar, 
  Zap, CheckCircle, HelpCircle, AlertTriangle, ChevronDown, 
  ChevronUp, Mic, Building2, Star
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



export default function FeesPage() {
  const [feeData, setFeeData] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    fetchFeeData();
  }, []);

  const fetchFeeData = async () => {
    try {
      const [feesRes, explanationRes, onboardingRes] = await Promise.all([
        fetch(`${API}/platform/fees`),
        fetch(`${API}/platform/fees/explanation`),
        fetch(`${API}/platform/fees/onboarding`)
      ]);

      if (feesRes.ok) setFeeData(await feesRes.json());
      if (explanationRes.ok) setExplanation(await explanationRes.json());
      if (onboardingRes.ok) setOnboarding(await onboardingRes.json());
    } catch (error) {
      console.error('Failed to fetch fee data:', error);
    } finally {
      setLoading(false);
    }
  };

  const FeeCard = ({ fee, feeKey }) => {
    const icons = {
      fan_subscriptions: Users,
      event_tickets: Calendar,
      battle_entries: Mic,
      boosts: Zap,
      studio_referrals: Building2,
      studio_listing: Building2
    };
    const Icon = icons[feeKey] || DollarSign;

    return (
      <Card className="glass-card border-border p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Icon className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg mb-1">{fee.name}</h3>
            <p className="text-sm text-muted-foreground mb-3">{fee.description}</p>
            
            {fee.fee_type === 'percentage' && (
              <div className="flex items-center gap-4">
                <div className="bg-muted/30 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">Platform Fee</p>
                  <p className="text-2xl font-bold text-primary">{fee.platform_fee_percent}%</p>
                </div>
                <div className="bg-secondary/10 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">You Keep</p>
                  <p className="text-2xl font-bold text-secondary">{fee.artist_payout_percent || (100 - fee.platform_fee_percent)}%</p>
                </div>
              </div>
            )}
            
            {fee.fee_type === 'service' && (
              <Badge className="bg-amber-500/20 text-amber-400">
                Platform Service
              </Badge>
            )}
            
            {fee.disclaimer && (
              <p className="mt-3 text-xs text-amber-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {fee.disclaimer}
              </p>
            )}
          </div>
        </div>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="fees-page">
      <div className="max-w-5xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-secondary/20 text-secondary">
            <Shield className="w-3 h-3 mr-1" />
            Transparency First
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase mb-4">
            How We <span className="text-secondary">Make Money</span>
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            DMV Soundstage does not take money from streams. Here's exactly how our platform works.
          </p>
        </div>

        {/* Core Principle Card */}
        <Card className="glass-card border-secondary/30 p-8 mb-10">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
              <Music className="w-8 h-8 text-secondary" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold uppercase mb-3">
                Streams Are Free
              </h2>
              <p className="text-lg mb-4">
                {feeData?.core_principle}
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="bg-green-500/10 border-green-500/30 text-green-400">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  No stream fees
                </Badge>
                <Badge variant="outline" className="bg-green-500/10 border-green-500/30 text-green-400">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  No play fees
                </Badge>
                <Badge variant="outline" className="bg-green-500/10 border-green-500/30 text-green-400">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Fair charts
                </Badge>
              </div>
            </div>
          </div>
        </Card>

        {/* Artist-Facing Explanation */}
        {explanation && (
          <Card className="glass-card border-border p-6 mb-10">
            <div className="grid md:grid-cols-4 gap-6">
              {explanation.key_points?.map((point, index) => {
                const icons = { music: Music, chart: BarChart3, dollar: DollarSign, shield: Shield };
                const Icon = icons[point.icon] || CheckCircle;
                return (
                  <div key={index} className="text-center">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-bold mb-1">{point.title}</h3>
                    <p className="text-sm text-muted-foreground">{point.description}</p>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        <Tabs defaultValue="fees" className="mb-10">
          <TabsList className="bg-muted/50 mb-6 w-fit">
            <TabsTrigger value="fees" className="px-6">
              <DollarSign className="w-4 h-4 mr-2" />
              Fee Structure
            </TabsTrigger>
            <TabsTrigger value="tiers" className="px-6">
              <Star className="w-4 h-4 mr-2" />
              Tier Fees
            </TabsTrigger>
            <TabsTrigger value="faq" className="px-6">
              <HelpCircle className="w-4 h-4 mr-2" />
              FAQ
            </TabsTrigger>
          </TabsList>

          {/* Fee Structure Tab */}
          <TabsContent value="fees">
            <div className="grid md:grid-cols-2 gap-6">
              {feeData?.fees && Object.entries(feeData.fees).map(([key, fee]) => (
                <FeeCard key={key} fee={fee} feeKey={key} />
              ))}
            </div>
            
            {/* Transparency Rules */}
            <Card className="glass-card border-border p-6 mt-8">
              <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-secondary" />
                Transparency Rules
              </h3>
              <ul className="grid md:grid-cols-2 gap-3">
                {feeData?.transparency_rules?.map((rule, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </TabsContent>

          {/* Tier Fees Tab */}
          <TabsContent value="tiers">
            <Card className="glass-card border-border p-6">
              <h3 className="font-heading font-bold text-lg mb-4">Artist Tier Platform Fees</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Higher subscription tiers get lower platform fees on revenue-generating features.
              </p>
              <div className="space-y-3">
                {feeData?.artist_tier_fees && Object.entries(feeData.artist_tier_fees).map(([tierId, tier]) => (
                  <div key={tierId} className="flex items-center justify-between p-4 rounded-lg bg-muted/30">
                    <div>
                      <p className="font-bold">{tier.name}</p>
                      <p className="text-sm text-muted-foreground">${tier.price}/month</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary">{tier.platform_fee_percent}%</p>
                      <p className="text-xs text-muted-foreground">Platform fee</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                * Platform fees apply to fan subscriptions, event tickets, and battle earnings. Artist subscription fees go directly to the platform.
              </p>
            </Card>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq">
            <div className="space-y-4">
              {onboarding?.sections?.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card 
                    className={`glass-card border-border p-4 cursor-pointer transition-all ${
                      section.highlight ? 'border-secondary/30 bg-secondary/5' : ''
                    }`}
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold flex items-center gap-2">
                        {section.highlight && <Star className="w-4 h-4 text-secondary" />}
                        {section.question}
                      </h3>
                      {expandedFaq === index ? (
                        <ChevronUp className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                    <AnimatePresence>
                      {expandedFaq === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <p className="mt-4 text-muted-foreground">
                            {section.answer}
                          </p>
                          {section.items && (
                            <ul className="mt-3 space-y-1">
                              {section.items.map((item, i) => (
                                <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                </motion.div>
              ))}
            </div>
            
            <p className="text-center text-sm text-muted-foreground mt-8">
              {onboarding?.footer_copy}
            </p>
          </TabsContent>
        </Tabs>

        {/* Bottom CTA */}
        <Card className="glass-card border-primary/30 p-8 text-center">
          <h2 className="font-heading text-2xl font-bold uppercase mb-4">
            Ready to Join?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            DMV Soundstage keeps charts fair, fees transparent, and puts real fan engagement first.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button className="bg-secondary hover:bg-secondary/90" onClick={() => window.location.href = '/pricing'}>
              View Artist Tiers
            </Button>
            <Button variant="outline" onClick={() => window.location.href = '/charts'}>
              Explore Charts
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
